import { DOOR_WIDTH } from "data/products/glassPanels";
import { Line, Point } from "types/canvasTypes";

export interface Panel3D {
  position: [number, number, number];
  rotation: [number, number, number];
  dimensions: [number, number, number]; // width, height, depth
  type: 'glass' | 'door';
  hasDoor: boolean;
  numberOfDoors: number;
  material: 'black' | 'silver';
}

type EndpointConnections = {
  hasStartConnection: boolean;
  hasEndConnection: boolean;
};

function arePointsClose(a: Point, b: Point, tolerance: number) {
  return Math.hypot(a.x - b.x, a.y - b.y) <= tolerance;
}

function getEndpointConnections(lines: Line[]): EndpointConnections[] {
  const connections: EndpointConnections[] = lines.map(() => ({
    hasStartConnection: false,
    hasEndConnection: false,
  }));

  // Keep consistent with canvas endpoint snapping tolerance.
  const endpointTolerance = 15;

  for (let i = 0; i < lines.length; i += 1) {
    for (let j = 0; j < lines.length; j += 1) {
      if (i === j) {
        continue;
      }

      if (
        arePointsClose(lines[i].start, lines[j].start, endpointTolerance) ||
        arePointsClose(lines[i].start, lines[j].end, endpointTolerance)
      ) {
        connections[i].hasStartConnection = true;
      }

      if (
        arePointsClose(lines[i].end, lines[j].start, endpointTolerance) ||
        arePointsClose(lines[i].end, lines[j].end, endpointTolerance)
      ) {
        connections[i].hasEndConnection = true;
      }

      if (connections[i].hasStartConnection && connections[i].hasEndConnection) {
        break;
      }
    }
  }

  return connections;
}

/**
 * Convert 2D canvas lines to 3D panel data
 * Each line is divided into multiple individual glass panels
 */
export function convertLinesToPanels(
  lines: Line[],
  height: number,
  scale: number
): Panel3D[] {
  const panels: Panel3D[] = [];
  const glassThickness = 0.5; // inches
  const heightInInches = height;
  const endpointConnections = getEndpointConnections(lines);

  lines.forEach((line, lineIndex) => {
    // Skip lines without panels
    if (!line.pannels || line.pannels.length === 0) {
      return;
    }

    const panelSequence: Array<{ type: "glass" | "door"; length: number }> = [];
    let doorsInserted = 0;

    line.pannels.forEach((panel) => {
      panelSequence.push({ type: "glass", length: panel.length });

      if (panel.isRPannel && doorsInserted < line.numberOfDoors) {
        panelSequence.push({ type: "door", length: DOOR_WIDTH });
        doorsInserted += 1;
      }
    });

    while (doorsInserted < line.numberOfDoors) {
      panelSequence.push({ type: "door", length: DOOR_WIDTH });
      doorsInserted += 1;
    }

    // Calculate line direction
    const startX = line.start.x / scale;
    const startY = line.start.y / scale;
    const endX = line.end.x / scale;
    const endY = line.end.y / scale;

    const totalLineLength = Math.sqrt(
      Math.pow(endX - startX, 2) + Math.pow(endY - startY, 2)
    );

    const angle = Math.atan2(endY - startY, endX - startX);

    // Unit vector along the line
    const dx = (endX - startX) / totalLineLength;
    const dy = (endY - startY) / totalLineLength;

    const totalPanelsLength = panelSequence.reduce(
      (sum, segment) => sum + segment.length,
      0
    );
    const remainingSpace = Math.max(0, totalLineLength - totalPanelsLength);
    const connection = endpointConnections[lineIndex];

    // In 3D, keep connected corners visually closed by absorbing any
    // residual line length into edge panels at connected endpoints.
    let extendStart = 0;
    let extendEnd = 0;
    let currentOffset = 0;
    if (remainingSpace > 0 && connection) {
      if (connection.hasStartConnection && connection.hasEndConnection) {
        extendStart = remainingSpace / 2;
        extendEnd = remainingSpace - extendStart;
      } else if (connection.hasStartConnection) {
        extendStart = remainingSpace;
      } else if (connection.hasEndConnection) {
        extendEnd = remainingSpace;
      } else {
        currentOffset = remainingSpace / 2;
      }
    }

    const panelCount = panelSequence.length;
    panelSequence.forEach((segment, panelIndex) => {
      const isFirstPanel = panelIndex === 0;
      const isLastPanel = panelIndex === panelCount - 1;
      const panelLength =
        segment.length +
        (isFirstPanel ? extendStart : 0) +
        (isLastPanel ? extendEnd : 0);

      // Calculate panel center position along the line
      const panelCenter = currentOffset + panelLength / 2;

      const centerX = startX + dx * panelCenter;
      const centerY = startY + dy * panelCenter;

      const isDoor = segment.type === "door";

      panels.push({
        position: [centerX, heightInInches / 2, centerY],
        rotation: [0, -angle, 0],
        dimensions: [panelLength, heightInInches, glassThickness],
        type: isDoor ? 'door' : 'glass',
        hasDoor: isDoor || false,
        numberOfDoors: isDoor ? 1 : 0,
        material: 'silver',
      });

      // Add a small visual gap between panels on straight runs only;
      // corner-connected lines keep panels flush to close the joint.
      const isStraightLine = !connection?.hasStartConnection && !connection?.hasEndConnection;
      currentOffset += panelLength + (isStraightLine ? 0.5 : -0.5);
    });
  });

  return panels;
}

/**
 * Calculate camera position based on design bounds
 */
export function calculateCameraPosition(panels: Panel3D[]): [number, number, number] {
  if (panels.length === 0) return [0, 50, 150];

  // Calculate bounding box
  let minX = Infinity, maxX = -Infinity;
  let minZ = Infinity, maxZ = -Infinity;
  let maxY = 0;

  panels.forEach((panel) => {
    const [x, y, z] = panel.position;
    const [width, height, depth] = panel.dimensions;

    minX = Math.min(minX, x - width / 2);
    maxX = Math.max(maxX, x + width / 2);
    minZ = Math.min(minZ, z - depth / 2);
    maxZ = Math.max(maxZ, z + depth / 2);
    maxY = Math.max(maxY, y + height / 2);
  });

  const centerX = (minX + maxX) / 2;
  const centerZ = (minZ + maxZ) / 2;
  const sizeX = maxX - minX;
  const sizeZ = maxZ - minZ;
  const maxSize = Math.max(sizeX, sizeZ);

  // Position camera at an angle to see the design nicely
  // Distance based on design size to fit everything in view
  const distance = Math.max(maxSize * 0.8, 100);

  // Camera positioned at 45 degree angle, slightly elevated
  const angle = Math.PI / 4; // 45 degrees
  const cameraX = centerX + Math.sin(angle) * distance;
  const cameraZ = centerZ + Math.cos(angle) * distance;
  const cameraY = maxY * 0.7; // Eye level, slightly below top

  return [cameraX, cameraY, cameraZ];
}
